import { cssRule, media, stylesheet } from 'typestyle';
import {
  BlackOpacity07,
  BlackOpacity60,
  BlackOpacity80,
  Gray100,
  typeCompact12MediumProperties,
  typeCompact14MediumProperties,
  White,
} from '@traceair/tds';
import { BREAKPOINT_LARGE } from '../../../layout/styles';
import { useSelectedMissionsTab } from '../useSelectedMissionsTab';
import { useFilteredProjects } from '../tabs/useFilteredProjects';
import { useFilteredMissions } from '../tabs/useFilteredMissions';
import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { MissionTab } from '../../../../consts/missionTab';
import * as turf from '@turf/turf';
import './custom-maplibre-gl.css';
import { formatDate } from '../../../../libs/dates';
import { ProjectData } from '../types';
import { getMissionHtmlId, getSiteHtmlId } from '../helpers/htmlIds';
import { getNextScheduledFlight, getNextScheduledGcpPlacementService } from '../helpers/nextSchedule';
import 'maplibre-gl/dist/maplibre-gl.css';

const CENTROID_SOURCE_ID = 'centroids';
const CLUSTERS_LAYER_ID = 'clusters';
const CLUSTER_COUNT_LAYER_ID = 'cluster-count';
const UNCLUSTERED_POINTS_LAYER_ID = 'unclustered-point';
const OSM_SOURCE_ID = 'osm';
const OSM_LAYER_ID = 'osm-layer';
const ZOOM_DURATION = 300;

const css = stylesheet({
  container: {
    borderRadius: 16,
    background: BlackOpacity07,
    flex: '1 1 auto',
    overflow: 'hidden',
    ...media({ maxWidth: BREAKPOINT_LARGE }, { display: 'none' }),
  },
});

cssRule('.missions-map-popup--customer', {
  marginBottom: 2,
  ...typeCompact14MediumProperties,
});

cssRule('.missions-map-popup--site', {
  ...typeCompact12MediumProperties,
});

cssRule('.missions-map-popup--next-mission', {
  marginTop: 6,
  color: Gray100,
  ...typeCompact12MediumProperties,
});

const createMapInstance = (container: HTMLDivElement, bounds: maplibregl.LngLatBounds) => {
  return new maplibregl.Map({
    container,
    // Shows the OpenStreetMap credit of the base layer.
    attributionControl: { compact: false },
    maxZoom: 17,
    style: {
      version: 8,
      sources: {
        [OSM_SOURCE_ID]: {
          type: 'raster',
          tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
          tileSize: 256,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
        },
      },
      layers: [
        {
          id: OSM_LAYER_ID,
          type: 'raster',
          source: OSM_SOURCE_ID,
        },
      ],
      glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    },
    zoom: 2,
    bounds,
    fitBoundsOptions: {
      padding: 10,
      animate: false,
    },
  });
};

const setupMapSources = (map: maplibregl.Map, geojson: GeoJSON.GeoJSON) => {
  map.addSource(CENTROID_SOURCE_ID, {
    type: 'geojson',
    data: geojson,
    cluster: true,
    clusterMaxZoom: 14,
    clusterRadius: 50,
    generateId: true,
  });
};

const setupMapLayers = (map: maplibregl.Map) => {
  // Clusters layer
  map.addLayer({
    id: CLUSTERS_LAYER_ID,
    type: 'circle',
    source: CENTROID_SOURCE_ID,
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': ['case', ['boolean', ['feature-state', 'hover'], false], BlackOpacity80, BlackOpacity60],
      'circle-radius': ['step', ['get', 'point_count'], 20, 10, 30, 50, 40],
    },
  });

  // Cluster count labels
  map.addLayer({
    id: CLUSTER_COUNT_LAYER_ID,
    type: 'symbol',
    source: CENTROID_SOURCE_ID,
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['Open Sans Semibold'],
      'text-size': 14,
    },
    paint: {
      'text-color': White,
    },
  });

  // Unclustered points
  map.addLayer({
    id: UNCLUSTERED_POINTS_LAYER_ID,
    type: 'circle',
    source: CENTROID_SOURCE_ID,
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': 'transparent',
      'circle-radius': 3,
      'circle-stroke-width': 5,
      'circle-stroke-color': ['case', ['boolean', ['feature-state', 'hover'], false], BlackOpacity80, BlackOpacity60],
    },
  });
};

const setupPointClickHandlers = (
  mapInstance: maplibregl.Map,
  selectedMissionsTab: MissionTab,
  projects: ProjectData[],
) => {
  const handlePointClick = (e: maplibregl.MapLayerMouseEvent) => {
    if (!e.features?.length) return;

    const feature = e.features[0];
    const { index } = feature.properties as MapFeatureProperties;
    const project = projects[index];

    if (selectedMissionsTab === MissionTab.AllProjects) {
      window.location.hash = getSiteHtmlId(project.customer.id, project.site.id);
    } else {
      const nextScheduledFlight = getNextScheduledFlight(project.site.scheduledFlights);
      if (nextScheduledFlight) {
        window.location.hash = getMissionHtmlId(nextScheduledFlight.airtableRecordId);
      } else {
        const nextGcpPlacement = getNextScheduledGcpPlacementService(project.site.gcpPlacementServices);
        if (nextGcpPlacement) {
          window.location.hash = getMissionHtmlId(nextGcpPlacement.airtableRecordId);
        }
      }
    }
  };

  mapInstance.on('click', UNCLUSTERED_POINTS_LAYER_ID, handlePointClick);
};

const setupClusterClickHandlers = (mapInstance: maplibregl.Map) => {
  const handleClusterClick = (e: maplibregl.MapLayerMouseEvent) => {
    const features = mapInstance.queryRenderedFeatures(e.point, {
      layers: [CLUSTERS_LAYER_ID],
    });
    const clusterId = features[0]?.properties?.cluster_id;
    if (!clusterId) return;

    (mapInstance.getSource(CENTROID_SOURCE_ID) as maplibregl.GeoJSONSource)
      .getClusterExpansionZoom(clusterId)
      .then((zoom) => {
        mapInstance.easeTo({
          center: (features[0].geometry as GeoJSON.Point).coordinates as [number, number],
          zoom,
          duration: ZOOM_DURATION,
        });
      });
  };

  mapInstance.on('click', CLUSTERS_LAYER_ID, handleClusterClick);
};

const setupHoverStateHandlers = (mapInstance: maplibregl.Map) => {
  let hoveredFeatureId: string | number | null = null;

  const updateHoverState = (e: maplibregl.MapLayerMouseEvent, hover: boolean) => {
    if (!e.features?.length) return;

    const featureId = e.features[0].id;
    if (featureId === undefined) return;

    if (hoveredFeatureId !== null) {
      mapInstance.setFeatureState({ source: CENTROID_SOURCE_ID, id: hoveredFeatureId }, { hover: false });
    }
    hoveredFeatureId = featureId;
    mapInstance.setFeatureState({ source: CENTROID_SOURCE_ID, id: featureId }, { hover });
  };

  mapInstance.on('mouseenter', [CLUSTERS_LAYER_ID, UNCLUSTERED_POINTS_LAYER_ID], (e) => {
    mapInstance.getCanvas().style.cursor = 'pointer';
    updateHoverState(e, true);
  });
  mapInstance.on('mouseleave', [CLUSTERS_LAYER_ID, UNCLUSTERED_POINTS_LAYER_ID], () => {
    mapInstance.getCanvas().style.cursor = '';
    if (hoveredFeatureId !== null) {
      mapInstance.setFeatureState({ source: CENTROID_SOURCE_ID, id: hoveredFeatureId }, { hover: false });
      hoveredFeatureId = null;
    }
  });
};

const setupPopupHandlers = (mapInstance: maplibregl.Map, projects: ProjectData[]) => {
  let hoverTimeout: NodeJS.Timeout | null = null;
  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    className: 'missions-map-popup',
    offset: 15,
  });

  mapInstance.on('mouseenter', UNCLUSTERED_POINTS_LAYER_ID, (e) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);
    hoverTimeout = setTimeout(() => {
      if (!e.features?.length) return;

      const feature = e.features[0];

      const { index } = feature.properties as MapFeatureProperties;
      const project = projects[index];
      const nextScheduledFlight = getNextScheduledFlight(project.site.scheduledFlights);
      const nextScheduledGcp = getNextScheduledGcpPlacementService(project.site.gcpPlacementServices);
      const flightDate = nextScheduledFlight?.flightDateIso ? new Date(nextScheduledFlight.flightDateIso) : null;
      const gcpDate = nextScheduledGcp?.servicesPerformedDateIso
        ? new Date(nextScheduledGcp.servicesPerformedDateIso)
        : null;

      let nextMissionDate: Date | null;
      if (flightDate && gcpDate) {
        nextMissionDate = flightDate < gcpDate ? flightDate : gcpDate;
      } else {
        nextMissionDate = flightDate || gcpDate;
      }

      if (feature.geometry?.type === 'Point') {
        let html = `
              <div class="missions-map-popup--customer">${project.customer.name}</div>
              <div class="missions-map-popup--site">${project.site.name}</div>
            `;

        if (nextMissionDate) {
          html += `<div class="missions-map-popup--next-mission">Next Mission: ${formatDate({
            date: nextMissionDate,
          })}</div>`;
        }

        popup
          .setLngLat(feature.geometry.coordinates as [number, number])
          .setHTML(html)
          .addTo(mapInstance);
      }
    }, 200);
  });

  mapInstance.on('mouseleave', UNCLUSTERED_POINTS_LAYER_ID, () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      hoverTimeout = null;
    }
    popup.remove();
  });
};

interface MapFeatureProperties {
  index: number;
}

export const MissionsMap = () => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const selectedMissionsTab = useSelectedMissionsTab();
  const allProjects = useFilteredProjects();
  const { scheduledProjects } = useFilteredMissions();

  const projects = selectedMissionsTab === MissionTab.AllProjects ? allProjects : scheduledProjects;

  useEffect(() => {
    if (!mapContainer.current) return;

    const projectsWithBoundaries = projects.filter((p) => !!p.site.boundary?.geometry);

    const features = projectsWithBoundaries.map((project, index) => {
      const centroid = turf.centroid(project.site.boundary!);

      return {
        type: 'Feature',
        geometry: centroid.geometry,
        properties: {
          index: index,
        } as MapFeatureProperties,
      };
    });

    const bounds = new maplibregl.LngLatBounds();
    features.forEach((f) => bounds.extend(f.geometry.coordinates as [number, number]));

    if (bounds.isEmpty()) {
      // Mainland USA
      bounds.extend([-125, 24, -66, 49]);
    }

    const geojson = {
      type: 'FeatureCollection',
      features,
    } as GeoJSON.GeoJSON;

    const mapInstance = createMapInstance(mapContainer.current, bounds);

    mapInstance.on('load', () => {
      setupMapSources(mapInstance, geojson);
      setupMapLayers(mapInstance);
      setupPointClickHandlers(mapInstance, selectedMissionsTab, projectsWithBoundaries);
      setupClusterClickHandlers(mapInstance);
      setupHoverStateHandlers(mapInstance);
      setupPopupHandlers(mapInstance, projectsWithBoundaries);
    });

    return () => mapInstance.remove();
  }, [projects.map(({ customer }) => customer.id).toString(), selectedMissionsTab]);

  return <div ref={mapContainer} className={css.container} data-intercom-target="MissionsMap" />;
};
